import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, RotateCcw, Shield, ShoppingCart, Receipt, UtensilsCrossed, Users, BarChart3, Settings, History } from 'lucide-react';
import { usePermissions, type UserPermission } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

// Permissions that should only be visible to superadmins
const SUPERADMIN_ONLY_PERMISSIONS = ['audit.view_superadmin', 'audit.purge'];

interface PermissionEditorProps {
  userId: string;
  userRole: string;
  disabled?: boolean;
}

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  pos: ShoppingCart,
  orders: Receipt,
  menu: UtensilsCrossed,
  customers: Users,
  reports: BarChart3,
  settings: Settings,
  team: Users,
  audit: History,
};

const CATEGORY_LABELS: Record<string, string> = {
  pos: 'Point of Sale',
  orders: 'Orders',
  menu: 'Menu',
  customers: 'Customers',
  reports: 'Reports',
  settings: 'Settings',
  team: 'Team Management',
  audit: 'Audit Logs',
};

export function PermissionEditor({ userId, userRole, disabled }: PermissionEditorProps) {
  const { isSuperadmin } = useAuth();
  const {
    permissionsByCategory,
    updateUserPermission,
    resetUserPermissions,
    rolePermissions,
  } = usePermissions();

  const [localPermissions, setLocalPermissions] = useState<Record<string, boolean>>({});
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<string | null>(null);

  // Fetch user's current permissions
  const { data: userPermissions, isLoading, refetch } = useQuery({
    queryKey: ['user-permissions-edit', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_user_permissions', { p_user_id: userId } as any);

      if (error) throw error;
      return data as UserPermission[];
    },
    enabled: !!userId,
  });

  // Initialize local state when permissions load
  useEffect(() => {
    if (userPermissions) {
      const perms: Record<string, boolean> = {};
      const ovr: Record<string, boolean> = {};
      
      userPermissions.forEach(p => {
        perms[p.permission_code] = p.granted;
        if (p.is_override) {
          ovr[p.permission_code] = p.granted;
        }
      });
      
      setLocalPermissions(perms);
      setOverrides(ovr);
    }
  }, [userPermissions]);

  // Get default permission for role
  const getRoleDefault = (permissionCode: string): boolean => {
    const rolePerms = rolePermissions[userRole] || [];
    return rolePerms.includes(permissionCode) || rolePerms.includes('*');
  };

  // Handle permission toggle
  const handleToggle = async (permissionCode: string, currentValue: boolean) => {
    if (disabled) return;
    
    const newValue = !currentValue;
    const roleDefault = getRoleDefault(permissionCode);
    
    setSaving(permissionCode);
    
    try {
      // If new value matches role default, remove the override
      if (newValue === roleDefault && overrides[permissionCode] !== undefined) {
        await updateUserPermission.mutateAsync({
          userId,
          permissionCode,
          granted: null, // Remove override
        });
        
        setOverrides(prev => {
          const next = { ...prev };
          delete next[permissionCode];
          return next;
        });
      } else {
        // Set override
        await updateUserPermission.mutateAsync({
          userId,
          permissionCode,
          granted: newValue,
        });
        
        setOverrides(prev => ({
          ...prev,
          [permissionCode]: newValue,
        }));
      }
      
      setLocalPermissions(prev => ({
        ...prev,
        [permissionCode]: newValue,
      }));
      
      refetch();
    } catch (error) {
      console.error('Failed to update permission:', error);
    } finally {
      setSaving(null);
    }
  };

  // Reset all overrides
  const handleResetAll = async () => {
    if (disabled) return;
    
    setSaving('reset');
    try {
      await resetUserPermissions.mutateAsync(userId);
      setOverrides({});
      refetch();
    } catch (error) {
      console.error('Failed to reset permissions:', error);
    } finally {
      setSaving(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasOverrides = Object.keys(overrides).length > 0;

  // Filter out superadmin-only permissions for non-superadmins
  const filteredPermissionsByCategory = Object.entries(permissionsByCategory).reduce(
    (acc, [category, permissions]) => {
      const filteredPerms = isSuperadmin
        ? permissions
        : permissions.filter(p => !SUPERADMIN_ONLY_PERMISSIONS.includes(p.code));

      if (filteredPerms.length > 0) {
        acc[category] = filteredPerms;
      }
      return acc;
    },
    {} as typeof permissionsByCategory
  );

  const categories = Object.keys(filteredPermissionsByCategory);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Customize permissions beyond the default {userRole} role.
        </p>
        {hasOverrides && !disabled && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetAll}
            disabled={saving === 'reset'}
          >
            {saving === 'reset' ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-2" />
            )}
            Reset to Defaults
          </Button>
        )}
      </div>

      <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
        {categories.map(category => {
          const permissions = filteredPermissionsByCategory[category];
          const Icon = CATEGORY_ICONS[category] || Shield;
          const label = CATEGORY_LABELS[category] || category;

          return (
            <div key={category} className="space-y-2">
              <div className="flex items-center gap-2 sticky top-0 bg-background py-1">
                <Icon className="h-4 w-4 text-muted-foreground" />
                <h4 className="font-medium text-sm">{label}</h4>
              </div>
              
              <div className="border rounded-lg divide-y">
                {permissions.map(permission => {
                  const isGranted = localPermissions[permission.code] ?? getRoleDefault(permission.code);
                  const hasOverride = overrides[permission.code] !== undefined;
                  const roleDefault = getRoleDefault(permission.code);
                  const isSaving = saving === permission.code;

                  return (
                    <div
                      key={permission.id}
                      className={cn(
                        'flex items-center justify-between p-3',
                        hasOverride && 'bg-primary/5'
                      )}
                    >
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{permission.name}</span>
                          {hasOverride && (
                            <Badge variant="outline" className="text-xs">
                              Override
                            </Badge>
                          )}
                        </div>
                        {permission.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {permission.description}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Role default: {roleDefault ? 'Granted' : 'Denied'}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {isSaving && (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                        <Switch
                          checked={isGranted}
                          onCheckedChange={() => handleToggle(permission.code, isGranted)}
                          disabled={disabled || isSaving}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {categories.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No permissions available
        </p>
      )}
    </div>
  );
}

